import * as moment from 'moment';
import * as React from 'react';
import { useMemo } from 'react';
import { Button, Icon, Popup } from 'semantic-ui-react';
import { Table as WWTable } from '@120wateraudit/waterworks';

// Types
import { Badge, BadgeVariant } from '@120wateraudit/envirio-components';
import { UserInvitation } from 'src/types/AccountManagementTypes';
import { UserInvitationStatus } from 'src/types/UserInvitationStatus';
import { useGetAccountInvitesQuery } from 'src/services';
import { useTableState } from 'src/hooks/useTableState';

interface Props {
  accountId: number;
  onInviteClicked: () => void;
  onInviteUrlCopy: (inviteCode: string) => void;
  onResendClicked: (inviteId: number, refetch: () => void) => void;
  onRevokeClicked: (inviteId: number, refetch: () => void) => void;
}

const UserInvitationStatusLabel = ({
  status
}: {
  status: UserInvitationStatus;
}) => {
  let variant: BadgeVariant = 'primary';
  let text: string;

  switch (status) {
    case UserInvitationStatus.Accepted:
      variant = 'success';
      text = 'Accepted';
      break;

    case UserInvitationStatus.Revoked:
      variant = 'error';
      text = 'Revoked';
      break;

    case UserInvitationStatus.New:
    default:
      text = 'New';
  }

  return <Badge variant={variant}>{text}</Badge>;
};

const isExpired = (expiresOn: string) =>
  moment()
    .utc()
    .isAfter(expiresOn);

const AccountUserInvites = ({
  accountId,
  onInviteClicked,
  onInviteUrlCopy,
  onResendClicked,
  onRevokeClicked
}: Props) => {
  const {
    params,
    setPageSize,
    setPageNumber,
    setSearchTermWrapper
  } = useTableState({ searchQueryType: 'search' });
  const { data: response, isFetching, refetch } = useGetAccountInvitesQuery({
    accountId,
    paramsData: params
  });

  const columns = useMemo(
    () => [
      {
        key: 'email',
        Header: 'Email',
        accessor: 'email'
      },
      {
        key: 'application',
        Header: 'Application',
        accessor: 'application'
      },
      {
        key: 'createdOn',
        Header: 'Created',
        accessor: ({ createdOn }: UserInvitation) =>
          createdOn ? moment(createdOn).format('MM/DD/YYYY') : ''
      },
      {
        key: 'expiresOn',
        Header: 'Expires',
        accessor: ({ expiresOn }: UserInvitation) => {
          const expired =
            expiresOn && isExpired(expiresOn) ? (
              <span style={{ marginRight: '10px' }}>
                <Badge variant="error">Expired</Badge>
              </span>
            ) : null;
          const formatted = expiresOn
            ? moment(expiresOn).format('MM/DD/YYYY')
            : '';
          return (
            <>
              {expired}
              {formatted}
            </>
          );
        }
      },
      {
        key: 'status',
        Header: 'Status',
        accessor: ({ status }: UserInvitation) => (
          <UserInvitationStatusLabel status={status} />
        )
      },
      {
        key: 'actions',
        Header: 'Actions',
        accessor: (invite: UserInvitation) => {
          if (invite.status !== UserInvitationStatus.New) {
            return null;
          }

          const expired = isExpired(invite.expiresOn);
          const actions: JSX.Element[] = [];

          if (!expired) {
            actions.push(
              <Button
                onClick={() => onRevokeClicked(invite.id, refetch)}
                key={`revoke-button-${invite.id}`}
                size="small"
                color="red"
                content="Revoke"
              />
            );

            actions.push(
              <Button
                onClick={() => onInviteUrlCopy(invite.inviteUrl)}
                key={`copy-link-button-${invite.id}`}
                size="small"
                color="blue"
                content="Copy Url"
              />
            );
          }

          actions.push(
            <Button
              onClick={() => onResendClicked(invite.id, refetch)}
              key={`resend-button-${invite.id}`}
              size="small"
              color="green"
              content="Resend"
            />
          );

          return (
            <div style={{ textAlign: 'center' }}>
              <Popup
                trigger={
                  <Button icon>
                    <Icon name="edit" />
                  </Button>
                }
                content={
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      padding: '2px'
                    }}>
                    {actions.map((action, i) => (
                      <React.Fragment key={action.key}>
                        {i > 0 && <div>&nbsp;</div>}
                        {action}
                      </React.Fragment>
                    ))}
                  </div>
                }
                on="click"
                position="top center"
              />
            </div>
          );
        }
      }
    ],
    [refetch, onRevokeClicked, onResendClicked, onInviteUrlCopy]
  );

  return (
    <div style={{ margin: '20px', width: '100%' }}>
      <Button
        icon
        labelPosition="right"
        onClick={onInviteClicked}
        primary
        style={{ marginBottom: '20px' }}>
        Invite User
        <Icon name="plus circle" />
      </Button>
      <WWTable
        columns={columns}
        data={response?.items || []}
        itemName="Invites"
        totalRecords={response?.count || 0}
        loading={isFetching}
        paginated
        page={params.pageNumber}
        pageSize={params.pageSize}
        onPageChanged={setPageNumber}
        onPageSizeChanged={setPageSize}
        searchable
        onSearchChanged={(term?: string) => setSearchTermWrapper(term || '')}
      />
    </div>
  );
};

export default AccountUserInvites;
